import React, { useEffect } from 'react';
import { Row, Col, Card, Statistic, Typography, Space, Button, Layout } from 'antd';
import {
  UserOutlined,
  DollarOutlined,
  TrophyOutlined,
  RiseOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useLeadStore from '../store/leadStore';
import useDealStore from '../store/dealStore';
import LeadConversionFunnel from '../components/LeadConversionFunnel';
import TargetSidebar from '../components/TargetSidebar';
import useSettingsStore from '../store/settingsStore';
import PageIcon from '../components/Layout/PageIcon';

const { Title, Text } = Typography;
const { Content, Sider } = Layout;

const Dashboard = () => {
  const navigate = useNavigate();
  const { stats: leadStats, fetchLeadStats } = useLeadStore();
  const { stats: dealStats, fetchDealStats, fetchUpcomingDeals, upcomingDeals } = useDealStore();
  const { formatCurrency } = useSettingsStore();

  useEffect(() => {
    fetchLeadStats();
    fetchDealStats();
    fetchUpcomingDeals(7); // Next 7 days
  }, [fetchLeadStats, fetchDealStats, fetchUpcomingDeals]);

  const quickActions = [
    {
      title: 'Add Lead',
      icon: <UserOutlined />,
      action: () => navigate('/leads/new'),
      color: '#1890ff'
    },
    {
      title: 'Create Deal',
      icon: <DollarOutlined />,
      action: () => navigate('/deals/new'),
      color: '#52c41a'
    }
  ];

  return (
    <Layout className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Content className="p-6">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <Title level={2} className="mb-2" style={{ display: 'flex', alignItems: 'center' }}>
                <PageIcon name="dashboard" /> Dashboard
              </Title>
              <Text className="text-gray-600 dark:text-gray-400">
                Welcome back! Here's what's happening with your CRM.
              </Text>
            </div>
            <Space>
              {quickActions.map((action, index) => (
                <Button
                  key={index}
                  type="primary"
                  icon={action.icon}
                  onClick={action.action}
                  className="touch-target focus-visible:focus"
                  style={{ backgroundColor: action.color, borderColor: action.color }}
                >
                  {action.title}
                </Button>
              ))}
            </Space>
          </div>

          {/* Key Metrics */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-soft hover:shadow-medium transition-shadow">
                <Statistic
                  title="Total Leads"
                  value={leadStats?.totalLeads || 0}
                  prefix={<UserOutlined className="text-blue-500" />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-soft hover:shadow-medium transition-shadow">
                <Statistic
                  title="Active Deals"
                  value={dealStats?.totalDeals || 0}
                  prefix={<DollarOutlined className="text-green-500" />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-soft hover:shadow-medium transition-shadow">
                <Statistic
                  title="Conversion Rate"
                  value={leadStats?.conversionRate || 0}
                  suffix="%"
                  prefix={<TrophyOutlined className="text-orange-500" />}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-soft hover:shadow-medium transition-shadow">
                <Statistic
                  title="Pipeline Value"
                  value={dealStats?.pipelineValue?.totalValue || 0}
                  prefix={<RiseOutlined className="text-purple-500" />}
                  precision={0}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Main Content Area - Lead Conversion Funnel & Activity */}
          <Row gutter={[16, 16]}>
            {/* Lead Conversion Funnel - 50% width */}
            <Col xs={24} lg={12}>
              <LeadConversionFunnel />
            </Col>

            {/* Recent Activity & Upcoming Deals - 50% width, stacked vertically */}
            <Col xs={24} lg={12}>
              <div className="space-y-4">
                <Card 
                  title="Recent Activity" 
                  className="shadow-soft"
                  extra={
                    <Button 
                      type="link" 
                      onClick={() => navigate('/leads')}
                      className="focus-visible:focus"
                    >
                      View All
                    </Button>
                  }
                >
                  <div className="space-y-4">
                    {leadStats?.statusBreakdown?.map((status) => (
                      <div key={status._id} className="flex justify-between items-center">
                        <Text className="capitalize">{status._id.replace('_', ' ')}</Text>
                        <div className="flex items-center space-x-2">
                          <Text strong>{status.count}</Text>
                          <div 
                            className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden"
                            role="progressbar"
                            aria-valuenow={status.count}
                            aria-valuemax={leadStats?.totalLeads || 1}
                          >
                            <div 
                              className="h-full bg-primary-500 transition-all duration-300"
                              style={{ 
                                width: `${(status.count / (leadStats?.totalLeads || 1)) * 100}%` 
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card 
                  title="Upcoming Deals (Next 7 Days)" 
                  className="shadow-soft"
                  extra={
                    <Button 
                      type="link" 
                      onClick={() => navigate('/deals')}
                      className="focus-visible:focus"
                    >
                      View All
                    </Button>
                  }
                >
                  <div className="space-y-4">
                    {upcomingDeals?.length > 0 ? (
                      upcomingDeals.slice(0, 5).map((deal) => (
                        <div key={deal._id} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div>
                            <Text strong className="block">{deal.title}</Text>
                            <Text className="text-sm text-gray-500 dark:text-gray-400">
                              {deal.contact?.fullName} • {formatCurrency(deal.value)}
                            </Text>
                          </div>
                          <div className="text-right">
                            <Text className="text-sm">
                              {new Date(deal.expectedCloseDate).toLocaleDateString()}
                            </Text>
                            <div className={`text-xs px-2 py-1 rounded-full ${
                              deal.daysUntilClose <= 2 ? 'bg-red-100 text-red-800' :
                              deal.daysUntilClose <= 5 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {deal.daysUntilClose} days
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <Text className="text-gray-500 dark:text-gray-400">
                          No upcoming deals in the next 7 days
                        </Text>
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            </Col>
          </Row>
        </div>
      </Content>
      
      {/* Right Sidebar - Target Management */}
      <Sider 
        width={320} 
        className="bg-white dark:bg-gray-900 shadow-lg"
        breakpoint="lg"
        collapsedWidth="0"
        trigger={null}
      >
        <TargetSidebar />
      </Sider>
    </Layout>
  );
};

export default Dashboard;
